import { toast } from 'react-hot-toast';

// Types
export interface DayData {
  date: string;
  available: boolean;
  price: number;
  bookingId?: string;
  guestName?: string;
}

export interface BlockedPeriod {
  id: string;
  startDate: string;
  endDate: string;
  reason: string;
}

export interface PricingRule {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  price: number;
  description?: string;
}

export interface WeekdayPricing {
  monday: number;
  tuesday: number;
  wednesday: number;
  thursday: number;
  friday: number;
  saturday: number;
  sunday: number;
}

export interface CalendarSettings {
  basePrice: number;
  weekdayPricing: WeekdayPricing;
  seasonalRules: PricingRule[];
  blockedPeriods: BlockedPeriod[];
}

// Mock data
const mockCalendarSettings: CalendarSettings = {
  basePrice: 250,
  weekdayPricing: {
    monday: 0,
    tuesday: 0,
    wednesday: 0,
    thursday: 0,
    friday: 20,
    saturday: 50,
    sunday: 20
  },
  seasonalRules: [
    {
      id: 'season1',
      name: 'School Holidays',
      startDate: '2025-06-01',
      endDate: '2025-06-30',
      price: 300,
      description: 'Higher rates during school holiday season'
    },
    {
      id: 'season2',
      name: 'Festive Season',
      startDate: '2025-12-15',
      endDate: '2026-01-15',
      price: 350,
      description: 'Peak rates during year-end holidays'
    }
  ],
  blockedPeriods: [
    {
      id: 'block1',
      startDate: '2025-06-05',
      endDate: '2025-06-10',
      reason: 'Personal use'
    },
    {
      id: 'block2',
      startDate: '2025-07-15',
      endDate: '2025-07-20',
      reason: 'Maintenance'
    }
  ]
};

// Mock bookings data
export interface CalendarBooking {
  id: string;
  guestName: string;
  checkIn: string;
  checkOut: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  total: number;
  paymentStatus: 'paid' | 'pending' | 'refunded';
  receiptUrl?: string;
}

const mockBookings: CalendarBooking[] = [
  {
    id: 'BK001',
    guestName: 'John Doe',
    checkIn: '2025-06-15',
    checkOut: '2025-06-20',
    status: 'confirmed',
    total: 1250,
    paymentStatus: 'paid',
    receiptUrl: '/receipts/receipt1.jpg'
  },
  {
    id: 'BK002',
    guestName: 'Jane Smith',
    checkIn: '2025-06-22',
    checkOut: '2025-06-25',
    status: 'pending',
    total: 750,
    paymentStatus: 'pending',
    receiptUrl: '/receipts/receipt2.jpg'
  },
  {
    id: 'BK003',
    guestName: 'Robert Johnson',
    checkIn: '2025-07-01',
    checkOut: '2025-07-07',
    status: 'confirmed',
    total: 1500,
    paymentStatus: 'paid',
    receiptUrl: '/receipts/receipt3.jpg'
  },
];

// Helper functions
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Calendar service
const calendarService = {
  // Get calendar data for a specific month
  getCalendarData: async (year: number, month: number): Promise<DayData[]> => {
    try {
      console.log(`Calendar service: Getting data for ${year}-${month+1}`);
      await delay(500); // Simulate API delay
      
      const settings = { ...mockCalendarSettings };
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const result: DayData[] = [];
      
      console.log(`Calendar service: Generating ${daysInMonth} days of data`);
      
      // Generate calendar data for each day in the month
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month, day);
        const dateString = date.toISOString().split('T')[0];
        const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
        
        // Default price is base price
        let price = settings.basePrice;
        
        // Add weekday adjustment
        const weekdayAdjustments = [
          settings.weekdayPricing.sunday,
          settings.weekdayPricing.monday,
          settings.weekdayPricing.tuesday,
          settings.weekdayPricing.wednesday,
          settings.weekdayPricing.thursday,
          settings.weekdayPricing.friday,
          settings.weekdayPricing.saturday
        ];
        price += weekdayAdjustments[dayOfWeek];
        
        // Check if date falls within any seasonal pricing rule
        for (const rule of settings.seasonalRules) {
          if (dateString >= rule.startDate && dateString <= rule.endDate) {
            price = rule.price;
            break;
          }
        }
        
        // Check if date is blocked
        let available = true;
        for (const period of settings.blockedPeriods) {
          if (dateString >= period.startDate && dateString <= period.endDate) {
            available = false;
            break;
          }
        }
        
        // Check if date has a booking
        let bookingId;
        let guestName;
        for (const booking of mockBookings) {
          if (
            dateString >= booking.checkIn && 
            dateString < booking.checkOut && 
            (booking.status === 'confirmed' || booking.status === 'pending')
          ) {
            available = false;
            bookingId = booking.id;
            guestName = booking.guestName;
            break;
          }
        }
        
        result.push({
          date: dateString,
          available,
          price,
          bookingId,
          guestName
        });
      }
      
      return result;
    } catch (error) {
      console.error('Error fetching calendar data:', error);
      throw new Error('Failed to fetch calendar data');
    }
  },
  
  // Get calendar settings
  getCalendarSettings: async (): Promise<CalendarSettings> => {
    try {
      await delay(300); // Simulate API delay
      return { ...mockCalendarSettings };
    } catch (error) {
      console.error('Error fetching calendar settings:', error);
      throw new Error('Failed to fetch calendar settings');
    }
  },
  
  // Update calendar settings
  updateCalendarSettings: async (settings: CalendarSettings): Promise<CalendarSettings> => {
    try {
      await delay(500); // Simulate API delay
      
      // In a real app, this would send the settings to the server
      Object.assign(mockCalendarSettings, settings);
      
      toast.success('Calendar settings updated successfully');
      return { ...mockCalendarSettings };
    } catch (error) {
      console.error('Error updating calendar settings:', error);
      toast.error('Failed to update calendar settings');
      throw new Error('Failed to update calendar settings');
    }
  },
  
  // Update a specific day's data
  updateDayData: async (dayData: DayData): Promise<DayData> => {
    try {
      await delay(300); // Simulate API delay
      
      // In a real app, this would update the day's data on the server
      // For now, we'll just return the updated data
      toast.success(`Updated ${dayData.date} successfully`);
      return { ...dayData };
    } catch (error) {
      console.error('Error updating day data:', error);
      toast.error('Failed to update day data');
      throw new Error('Failed to update day data');
    }
  },
  
  // Get bookings
  getBookings: async (): Promise<CalendarBooking[]> => {
    try {
      await delay(400); // Simulate API delay
      return [...mockBookings];
    } catch (error) {
      console.error('Error fetching bookings:', error);
      throw new Error('Failed to fetch bookings');
    }
  },
  
  // Update booking status
  updateBookingStatus: async (id: string, status: CalendarBooking['status']): Promise<CalendarBooking> => {
    try {
      await delay(400); // Simulate API delay
      
      const bookingIndex = mockBookings.findIndex(b => b.id === id);
      if (bookingIndex === -1) {
        throw new Error('Booking not found');
      }
      
      mockBookings[bookingIndex] = {
        ...mockBookings[bookingIndex],
        status
      };
      
      toast.success(`Booking ${id} ${status} successfully`);
      return { ...mockBookings[bookingIndex] };
    } catch (error) {
      console.error(`Error updating booking status:`, error);
      toast.error('Failed to update booking status');
      throw new Error('Failed to update booking status');
    }
  },
  
  // Add blocked period
  addBlockedPeriod: async (period: Omit<BlockedPeriod, 'id'>): Promise<BlockedPeriod> => {
    try {
      await delay(300); // Simulate API delay
      
      const id = `block${mockCalendarSettings.blockedPeriods.length + 1}`;
      const newPeriod = { ...period, id };
      
      mockCalendarSettings.blockedPeriods.push(newPeriod);
      
      toast.success('Blocked period added successfully');
      return newPeriod;
    } catch (error) {
      console.error('Error adding blocked period:', error);
      toast.error('Failed to add blocked period');
      throw new Error('Failed to add blocked period');
    }
  },
  
  // Delete blocked period
  deleteBlockedPeriod: async (id: string): Promise<void> => {
    try {
      await delay(300); // Simulate API delay
      
      const index = mockCalendarSettings.blockedPeriods.findIndex(p => p.id === id);
      if (index === -1) {
        throw new Error('Blocked period not found');
      }
      
      mockCalendarSettings.blockedPeriods.splice(index, 1);
      
      toast.success('Blocked period deleted successfully');
    } catch (error) {
      console.error('Error deleting blocked period:', error);
      toast.error('Failed to delete blocked period');
      throw new Error('Failed to delete blocked period');
    }
  },
  
  // Add seasonal pricing rule
  addPricingRule: async (rule: Omit<PricingRule, 'id'>): Promise<PricingRule> => {
    try {
      await delay(300); // Simulate API delay
      
      const id = `season${mockCalendarSettings.seasonalRules.length + 1}`;
      const newRule = { ...rule, id };
      
      mockCalendarSettings.seasonalRules.push(newRule);
      
      toast.success('Pricing rule added successfully');
      return newRule;
    } catch (error) {
      console.error('Error adding pricing rule:', error);
      toast.error('Failed to add pricing rule');
      throw new Error('Failed to add pricing rule');
    }
  },
  
  // Delete seasonal pricing rule
  deletePricingRule: async (id: string): Promise<void> => {
    try {
      await delay(300); // Simulate API delay
      
      const index = mockCalendarSettings.seasonalRules.findIndex(r => r.id === id);
      if (index === -1) {
        throw new Error('Pricing rule not found');
      }
      
      mockCalendarSettings.seasonalRules.splice(index, 1);
      
      toast.success('Pricing rule deleted successfully');
    } catch (error) {
      console.error('Error deleting pricing rule:', error);
      toast.error('Failed to delete pricing rule');
      throw new Error('Failed to delete pricing rule');
    }
  }
};

export default calendarService;
